import React, { useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { transLanguage } from '@op/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { channelColorMap } from '@op/constants';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_EC, DBO_V, DBO_Y } from '@op/utils/format';
import { useVersionLineChart } from '../../../../hooks/useVersionLineChart';
import { MetricItemType } from '../../LeftMetrics';

export default function CommonChart({
  filter,
  type,
  metricItem,
}: {
  filter: any;
  type: 'channel' | 'language' | 'all';
  metricItem: MetricItemType;
}) {
  const { loading, data } = useVersionLineChart({ filter, type, metricItem });

  const { formatMessage } = useLanguage();
  const eChartsRef = useRef<any>(null);
  const options = useMemo(() => {
    if (data?.isEmpty === true) {
      return {};
    }

    const { series, legend, xAxis, isMin } = data;

    const seriesData = series?.map((item) => {
      const { name, data } = item;

      if (type === 'channel') {
        const color = channelColorMap[name];
        return {
          name,
          data,
          type: 'line',
          itemStyle: { color },
          areaStyle: DBO_EC.gradientColor(color, 0, 0.4),
        };
      }
      return { ...item, type: 'line' };
    });

    const {
      format = (value: any) => DBO_V.reviews(value),
      name: itemName,
      isPercentage = false,
    } = metricItem || {};
 

    return {
      series: seriesData,
      legend: {
        data: legend,
        formatter: (params: any) => {
          if (type === 'channel' && params !== 'other') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }
          if (type === 'language' && params !== 'other') {
            return transLanguage(params);
          }
          if (params === 'other') {
            return formatMessage({ id: 'Others' });
          }

          return formatMessage(itemName);
        },
      },
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(params[0]?.axisValue, 'weekly');

          return formatChartParamsDate({
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'other';
              if (type === 'channel') {
                const { img, name } = channelInfo(item.seriesName);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${
                  isNoOther ? name : formatMessage({ id: 'Others' })
                }<span>`;
              }
              if (type === 'language' && isNoOther) {
                return transLanguage(item.seriesName);
              }

              if (!isNoOther) {
                return formatMessage({ id: 'Others' });
              }

              return formatMessage(metricItem.name);
            },
            valueFormat: (value) => format(value),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // if (!item.originValue) return null;
                const share = DBO_V.percentage(
                  Math.abs(item.originValue),
                  total,
                  2,
                );
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              if (!isPercentage && type !== 'all') {
                newRecords.unshift({
                  text: formatMessage({ id: 'Sum' }),
                  value: DBO_V.reviews(total),
                  share: total > 0 ? '100%' : '-',
                });
              }
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: leftTitle,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage(metricItem.name),
                },
              ];

              if (!isPercentage && type !== 'all') {
                columns.push({
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                });
              }

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },

      xAxis: {
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxis || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_Y.reviewsPercent(value, isPercentage, isMin);
            },
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
      ],
      grid: {
        left: 10,
        right: 5,
        top: 20,
      },
    };
  }, [data, formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: 'line' }}
      height={338}
      options={options}
      empty={data.isEmpty}
      ref={eChartsRef}
      loading={loading}
    ></EChartCommon>
  );
}
